import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param {string} dirty - The unsanitized HTML string
 * @returns {string} - Sanitized HTML safe for rendering
 */
export const sanitizeHTML = (dirty) => {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'span'],
    ALLOWED_ATTR: ['class']
  });
};

/**
 * Validates and sanitizes URLs
 * @param {string} url - The URL to validate
 * @returns {string} - Valid URL or empty string
 */
export const sanitizeURL = (url) => {
  if (!url) return '';
  
  try {
    const parsed = new URL(url);
    // Only allow http and https protocols
    return ['http:', 'https:'].includes(parsed.protocol) ? url : '';
  } catch {
    return '';
  }
};

/**
 * Sanitizes plain text by removing HTML tags
 * @param {string} text - The text to sanitize
 * @returns {string} - Sanitized text
 */
export const sanitizeText = (text) => {
  if (!text) return '';
  // Remove any HTML tags and trim whitespace
  return text.replace(/<[^>]*>/g, '').trim();
};

/**
 * Sanitizes email addresses
 * @param {string} email - The email to sanitize
 * @returns {string} - Sanitized email
 */
export const sanitizeEmail = (email) => {
  if (!email) return '';
  return email.toLowerCase().trim();
};

/**
 * Generic input sanitizer based on type
 * @param {string} input - The input to sanitize
 * @param {string} type - Type of input (text, html, url, email)
 * @returns {string} - Sanitized input
 */
export const sanitizeInput = (input, type = 'text') => {
  if (!input) return '';
  
  switch (type) {
    case 'html':
      return sanitizeHTML(input);
    case 'url':
      return sanitizeURL(input);
    case 'email':
      return sanitizeEmail(input);
    case 'text':
    default:
      return sanitizeText(input);
  }
};
